/**
 * The running turn's LIVE DRAFT on the web playground — the same rules as
 * the desktop chat (packages/desktop/src/renderer/liveDraft.ts), kept in step
 * by the tests on both sides.
 *
 * A step's text streams as the model writes it, but it is not the answer: the
 * answer arrives as `delta` frames only after every finish gate accepts it.
 * Narration from a step that moved on to tools stays as a muted note, a draft
 * a check sent back is marked so, and an accepted draft is dropped because
 * the reply carries the same text.
 */
export interface DraftNote {
  readonly step: number;
  readonly text: string;
  readonly outcome: "tools" | "revised";
}
export interface LiveDraftState {
  readonly current: { readonly step: number; readonly text: string } | null;
  readonly notes: readonly DraftNote[];
}
export type DraftFrame =
  | { readonly t: "draft"; readonly step: number; readonly text: string }
  | { readonly t: "draft_end"; readonly step: number; readonly outcome: "tools" | "answer" | "revised" };

export const EMPTY_DRAFT: LiveDraftState = { current: null, notes: [] };
const MAX_NOTES = 12;
const NOTE_CHARS = 280;

export function reduceDraft(state: LiveDraftState, frame: DraftFrame): LiveDraftState {
  if (frame.t === "draft") {
    const current = state.current && state.current.step === frame.step ? { step: frame.step, text: state.current.text + frame.text } : { step: frame.step, text: frame.text };
    return { ...state, current };
  }
  if (!state.current || state.current.step !== frame.step) return state;
  if (frame.outcome === "answer") return { ...state, current: null };
  const text = state.current.text.trim().replace(/\s+/g, " ");
  if (text === "") return { ...state, current: null };
  const note: DraftNote = { step: frame.step, text: text.length > NOTE_CHARS ? `${text.slice(0, NOTE_CHARS - 1)}…` : text, outcome: frame.outcome };
  return { current: null, notes: [...state.notes, note].slice(-MAX_NOTES) };
}
